import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Box } from '@mui/material';
import { MainLayout } from 'layouts/MainLayout';
import { voucherApi } from 'components/vouchers/voucherApi';
import { VoucherView } from 'components/vouchers/VoucherView';
import { MARKETING_VOUCHERS } from 'config/routes';
import logger from 'lib/logger';
import { isString } from 'lib/typeguards';
import { SkeletonComponent } from 'ui/skeleton/SkeletonComponent';
import { Voucher } from 'components/vouchers/models/VoucherModel';

const VouchersViewPage = () => {
    const [voucher, setVoucher] = React.useState<Voucher>(null);
    const navigate = useNavigate();
    const { voucherId } = useParams<{ voucherId: string }>();

    React.useEffect(() => {
        const load = async () => {
            try {
                if (!isString(voucherId)) {
                    logger.error(`Expected voucherId but got ${voucherId}`);
                    navigate(MARKETING_VOUCHERS);
                }
                const voucherResponse = await voucherApi.get(voucherId);
                if (!voucherResponse.ok) {
                    logger.error('Expected voucherResponse 200 but got ', voucherResponse);
                    navigate(MARKETING_VOUCHERS);
                }
                const newVoucher = voucherResponse.body;

                setVoucher(newVoucher);
            } catch (error) {
                logger.error('Voucher view page error', error);
                navigate(MARKETING_VOUCHERS);
                setVoucher(null);
            }
        };

        load();
    }, [navigate, voucherId]);

    const handleEditSuccess = React.useCallback((updatedVoucher: Voucher) => {
        setVoucher(old => ({ ...old, ...updatedVoucher }));
    }, []);

    if (!voucher) {
        return (
            <MainLayout
                breadcrumbs={[{ label: 'Vouchers', url: MARKETING_VOUCHERS }]}
                pageName={'Voucher Details'}
                pageDescription="View more information on your voucher"
            >
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Box display="flex" justifyContent="flex-end">
                            <SkeletonComponent height="36px" width="136px" count={1} variant="rectangular" />
                        </Box>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}
                    >
                        <SkeletonComponent height="350px" count={1} variant="rectangular" />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}
                    >
                        <SkeletonComponent height="350px" count={1} variant="rectangular" />
                    </Grid>
                </Grid>
            </MainLayout>
        );
    }

    return (
        <MainLayout
            breadcrumbs={[{ label: 'Vouchers', url: MARKETING_VOUCHERS }]}
            pageName={voucher.title || 'Voucher Details'}
            pageDescription="View more information on your voucher"
        >
            <VoucherView onEdit={handleEditSuccess} voucher={voucher} />
        </MainLayout>
    );
};

export default VouchersViewPage;
